import { observer } from "mobx-react-lite";

import { DATE_FORMATS, TIME_FORMATS } from "@bps/date-time";
import { ITextProps, Spinner, Stack, Text, TooltipHost } from "@bps/fluent-ui";
import { UserStatus } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { routes } from "@libs/routing/routes.ts";
import { CalendarEvent } from "@stores/booking/models/CalendarEvent.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { Navigate } from "@ui-components/navigation/Navigate.tsx";
import { UserFetcher } from "@ui-components/UserFetcher.tsx";

import { getAppointmentStatusFromCode } from "../utils.ts";

const TableCell: React.FunctionComponent<ITextProps> = props => (
  <Stack
    verticalAlign="center"
    styles={{ root: { height: "100%", overflow: "hidden" } }}
  >
    <Text {...props} />
  </Stack>
);

export const AppointmentInformationDate = observer(
  (props: { calendarEvent: CalendarEvent; onDismiss: () => void }) => {
    const { routing } = useStores();

    const { user } = props.calendarEvent;

    if (!user) return null;

    const onHandleApptClick = () => {
      const path = { pathName: routes.calendarEvents.basePath.pattern };
      const state = {
        locationId: props.calendarEvent.orgUnitId,
        userId: user.id,
        startDateTime: props.calendarEvent.startDateTime.toISO()
      };

      props.onDismiss();

      if (routing.match(routes.calendarEvents.basePath)) {
        routing.replace(path.pathName, state);
      } else {
        routing.push(path.pathName, state);
      }
    };

    const dateBlock = (
      <>
        <Stack>
          {props.calendarEvent.startDateTime.toFormat(
            DATE_FORMATS.DAY_NAME_LONG
          )}
        </Stack>
        <Stack>{props.calendarEvent.startDateTime.toDayDefaultFormat()}</Stack>
        <Stack>
          {props.calendarEvent.startDateTime.toFormat(
            TIME_FORMATS.DEFAULT_TIME_FORMAT
          )}
        </Stack>
      </>
    );

    return (
      <DataFetcher
        fetch={({ booking }) => booking.getUserAvailability(user.id)}
        fallback={<Spinner />}
      >
        {() => (
          <TableCell>
            {props.calendarEvent.cancellationReason ||
            user.status === UserStatus.Inactive ||
            !user?.availableOrgUnitIds?.includes(
              props.calendarEvent.orgUnitId
            ) ? (
              dateBlock
            ) : (
              <Navigate onClick={() => onHandleApptClick()}>
                {dateBlock}
              </Navigate>
            )}
          </TableCell>
        )}
      </DataFetcher>
    );
  }
);

export const AppointmentInformationApptType = observer(
  (props: { calendarEvent: CalendarEvent }) => (
    <TableCell>
      {props.calendarEvent.appointmentType?.text || "Not recorded"}
    </TableCell>
  )
);

export const AppointmentInformationDuration = observer(
  (props: { calendarEvent: CalendarEvent }) => (
    <TableCell styles={{ root: { textAlign: "right" } }}>
      {`${props.calendarEvent.duration.minutes} mins`}
    </TableCell>
  )
);

export const AppointmentInformationStatus = observer(
  (props: { calendarEvent: CalendarEvent }) => {
    const { booking } = useStores();
    const {
      calendarEvent: {
        appointmentStatus,
        cancellationReasonId,
        cancellationText,
        status
      }
    } = props;

    const fetch = async () => {
      const cancelledReasonId = cancellationReasonId;
      if (cancelledReasonId) {
        return await booking.getCalendarCancellationReason(cancelledReasonId);
      }
      return undefined;
    };

    return (
      <TableCell>
        {appointmentStatus && status ? (
          <DataFetcher
            fetch={fetch}
            fallback={<Spinner />}
            refetchId={cancellationReasonId}
          >
            {cancelledReason => {
              const cancelReasonText =
                cancelledReason?.text === "Other"
                  ? `${cancelledReason?.text}: ${cancellationText}`
                  : cancelledReason?.text;
              return (
                <TooltipHost content={cancelReasonText ?? ""}>
                  {getAppointmentStatusFromCode(appointmentStatus, status)}
                </TooltipHost>
              );
            }}
          </DataFetcher>
        ) : undefined}
      </TableCell>
    );
  }
);

export const AppointmentInformationProvider = observer(
  (props: { calendarEvent: CalendarEvent }) => (
    <TableCell>
      <UserFetcher userId={props.calendarEvent.userId}>
        {user => user.fullName}
      </UserFetcher>
    </TableCell>
  )
);

export const AppointmentInformationLocation = observer(
  (props: { calendarEvent: CalendarEvent }) => {
    const { core } = useStores();
    return (
      <TableCell>
        {core.getLocationName(props.calendarEvent.orgUnitId)}
      </TableCell>
    );
  }
);

export const AppointmentInformationClaim = observer(
  (props: { calendarEvent: CalendarEvent }) => (
    <TableCell>{props.calendarEvent.claim?.claimNumber}</TableCell>
  )
);

export const AppointmentCancellationReason = observer(
  (props: { calendarEvent: CalendarEvent }) => (
    <TableCell>
      {props.calendarEvent.cancellationReason?.text === "Other" ? (
        <Text>{props.calendarEvent.cancellationText}</Text>
      ) : (
        props.calendarEvent.cancellationReason?.text
      )}
    </TableCell>
  )
);
